import type { TourRouteCoordinate } from '@/features/tour/tour-route-map-utils'
import type { IPOI } from '@/shared/api/types/poi-types'
import type {
  CreateTourPayload,
  ITourPoi,
  ITourTranslation,
  TourBasicInfoState,
} from '@/shared/api/types/tour-types'
import {
  getPoiCoordinatesFromSelection,
  normalizeTourCoordinates,
} from '@/features/tour/tour-route-map-utils'
import { MIN_TOUR_ROUTE_COORDINATES } from '@/shared/constants/tour-form'

/** Trims name/description so whitespace-only differences never count as an edit or reach the API. */
export function normalizeTourTranslations(translations: ITourTranslation[]): ITourTranslation[] {
  return translations.map((item) => ({
    ...item,
    name: item.name.trim(),
    description: item.description.trim(),
  }))
}

/**
 * VI: Ưu tiên toạ độ user chỉnh trên map; nếu thiếu điểm thì dùng toạ độ suy ra từ các POI đã chọn.
 * EN: Prefer the coordinates edited on the map; if there are too few points, fall back to the ones inferred from the selected POIs.
 */
export function resolveRouteCoordinates(
  routeCoordinates: TourRouteCoordinate[],
  selectedPois: ITourPoi[],
  poiMap: Record<string, IPOI>,
): TourRouteCoordinate[] {
  const fromMap = normalizeTourCoordinates(routeCoordinates)
  if (fromMap.length >= MIN_TOUR_ROUTE_COORDINATES) {
    return fromMap
  }
  return normalizeTourCoordinates(getPoiCoordinatesFromSelection(selectedPois, poiMap))
}

export interface BuildCreateTourPayloadInput {
  basicInfo: TourBasicInfoState
  translations: ITourTranslation[]
  selectedPois: ITourPoi[]
  coordinates: TourRouteCoordinate[]
  banner: string
  thumbnail: string
  isActive: boolean
}

/** Body of `POST /tours`. `isActive` is only sent on create; an existing tour is (de)activated from the list. */
export function buildCreateTourPayload(input: BuildCreateTourPayloadInput): CreateTourPayload {
  return {
    tenantDestinationId: input.basicInfo.tenantDestinationId,
    localizedData: input.translations,
    poiIds: input.selectedPois.map((item) => item.poiId),
    duration: input.basicInfo.duration,
    coordinates: input.coordinates,
    banner: input.banner,
    thumbnail: input.thumbnail,
    ticketLink: input.basicInfo.ticketLink.trim() || null,
    isFavorite: input.basicInfo.isFavorite,
    isActive: input.isActive,
  }
}

/** Body of `PATCH /tours/:id` for the basic-info section (route coordinates are saved separately). */
export function buildBasicInfoUpdatePayload(
  basicInfo: TourBasicInfoState,
): Pick<CreateTourPayload, 'duration' | 'tenantDestinationId' | 'ticketLink' | 'isFavorite'> {
  return {
    duration: basicInfo.duration,
    tenantDestinationId: basicInfo.tenantDestinationId,
    ticketLink: basicInfo.ticketLink.trim() || null,
    isFavorite: basicInfo.isFavorite,
  }
}
